import React, { useEffect, useState, useRef } from "react";
import { Button, Card, Form, Input, Row, Col, Space, Typography, notification, Spin } from "antd";
import { formatCurrency, isNilEmptyOrZeroLength, isNilOrEmpty, safeParseInt } from "@salespark/toolkit";
import { createNewStocksInStoreByContext, getMovementsFromStoreByContext, offlineHandler, saveMovementsToStore } from "utils/offlineCache";
import { PlusCircleOutlined } from "@ant-design/icons";
import { getCurrentPrice } from "utils/helper";
import PageLayoutWrapper from "components/PageLayoutWrapper";
import SalesDrawer from "./SalesDrawer";
import RemoveQuantityModal from "./RemoveQuantityModal";
import { parseApiMovements } from "./helpers";
import CateringDrawer from "./CateringDrawer";
import TransferDrawer from "./TransferDrawer";
import { useAuth } from "services/Auth";
import dayjs from "dayjs";
import useSelectedService from "hooks/useSelectedService";
import { locationTracker } from "utils/location/geolocation";
import { generateActionReference } from "utils/location/record";
import { LOCATION_ACTIONS } from "constants/location";

const getMovementByWare = (movements, wareId) => movements?.find((m) => m.wares === wareId);
const mergeMovements = (initialMovements, storedMovements) => {
  return initialMovements.map((initial) => {
    const stored = storedMovements.find((m) => m.wares === initial.wares);

    if (!stored) return initial;

    return {
      ...initial,
      ...stored,

      consumption: {
        ...initial.consumption,
        ...stored.consumption,
      },

      sale_movement_unit: {
        ...initial.sale_movement_unit,
        ...stored.sale_movement_unit,
      },

      sale_sale_unit: {
        ...initial.sale_sale_unit,
        ...stored.sale_sale_unit,
      },
    };
  });
};

const { Text } = Typography;

const InlineQuantity = ({ style, value = 0, onPlus, onMinus }) => (
  <Space.Compact style={{ display: "flex" }}>
    <Button size="large" onClick={onMinus} style={{ backgroundColor: style }}>
      −
    </Button>
    <Input value={value} readOnly style={{ width: 50, textAlign: "center", backgroundColor: style }} />
    <Button size="large" onClick={onPlus} style={{ backgroundColor: style }}>
      +
    </Button>
  </Space.Compact>
);

const groupBoxStyle1 = {
  background: "#f6fffb",
  border: "1px solid #b7ebd3",
  borderRadius: 8,
  height: "100%",
};

const groupBoxStyle2 = {
  background: "#fff7e6",
  border: "1px solid #ffd591",
  borderRadius: 8,
  height: "100%",
};

function ConsumptionsSales() {
  const { auth } = useAuth();
  const apiClient = offlineHandler(auth.data);
  const [form] = Form.useForm();
  const isMountedRef = useRef(true);
  const submitOnUnmountRef = useRef(async () => {});
  const [isLoading, setIsLoading] = useState(false);

  const [wares, setWares] = useState([]);
  const waresRef = useRef([]);
  const stocksRef = useRef([]);

  const [stocks, setStocks] = useState([]);
  const [salesTotals, setSalesTotals] = useState(0);

  const [selectedWare, setSelectedWare] = useState(null);
  const [saleDrawerVisible, setSaleDrawerVisible] = useState(false);
  const [removeModalVisible, setRemoveModalVisible] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [hasCatering, setHasCatering] = useState(false);
  const [cateringDrawerVisible, setCateringDrawerVisible] = useState(false);
  const [productForService, setProductForService] = useState(null);
  const [productForServiceDrawerVisible, setProductForServiceDrawerVisible] = useState(false);
  const authUserName = auth?.data?.name || "Unknown User";

  const { selectedService } = useSelectedService();

  const movements = Form.useWatch("movements", form);
  const selectedDate = Form.useWatch("date", form);

  useEffect(() => {
    waresRef.current = wares;
  }, [wares]);

  useEffect(() => {
    if (isNilOrEmpty(selectedService) || isNilOrEmpty(selectedService?.equipment?.[0]?._id)) return;
    if (wares.length === 0) return;
    const equipment = selectedService.equipment[0];
    if (!equipment) return;
    for (const ware of wares) {
      const newStock = getStockQuantity(ware._id);
      const oldStock = stocks.find((s) => s.wares._id === ware._id)?.counted_quantity;
      if (safeParseInt(newStock) !== safeParseInt(oldStock)) {
        const existing = stocksRef.current.find((s) => s.wares === ware._id && s.equipment === equipment);
        if (existing) {
          existing.quantity = safeParseInt(newStock);
        } else {
          stocksRef.current.push({
            wares: ware._id,
            equipment: equipment,
            quantity: safeParseInt(newStock),
          });
        }
      }
    }
  }, [movements, stocks, wares, selectedService]);

  useEffect(() => {
    isMountedRef.current = true;

    if (isNilOrEmpty(selectedService) || isNilOrEmpty(selectedService.start_date_effective)) return;
    if (selectedService?.entries.some((e) => e.catering === true)) setHasCatering(true);
    if (selectedService?.entries.some((e) => e.product.product_for_service !== null)) setProductForService(true);

    form.setFieldsValue({
      date: dayjs(selectedService.final_start_date),
      service_id: selectedService.service_id_raw,
      equipment: selectedService.equipment.length === 1 ? selectedService.equipment[0]._id : null,
    });
    loadData();

    return () => {
      isMountedRef.current = false;
    };
  }, [selectedService]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const referenceDate = selectedDate || dayjs(selectedService.final_start_date);
      // --- Sales totals ---
      const salesResponse = await apiClient.getMany("/goods/sales");
      if (!salesResponse.status) {
        notification.error({ message: "Erro", description: "Erro ao carregar movimentos" });
        await apiClient.post("/error-capture", {
          error: salesResponse.data,
          action: "/ConsumptionsSales/loadData/ getMany /goods/sales",
          timestamp: new Date().toISOString(),
        });
        return;
      }

      // Fix: operator precedence bug in original — wrapping the ternary
      const equipment = selectedService.equipment.length === 1 ? selectedService.equipment[0]._id : null;
      const key = `${equipment}_${dayjs().format("DD-MM-YYYY")}`;
      const previousTotals = salesResponse.data.find((s) => s._id === key);
      setSalesTotals(previousTotals?.total_sell_price ?? 0);

      // --- Locally stored movements ---
      const previousMovementsResponse = await getMovementsFromStoreByContext({
        user_id: auth?.data?._id || null,
        service_id_raw: selectedService.service_id_raw,
        equipment: selectedService.equipment[0]?._id || null,
        date: referenceDate,
      });
      const firstEntry = previousMovementsResponse?.data?.data;

      let storedMovements = [];
      if (
        previousMovementsResponse.status === true &&
        previousMovementsResponse.data &&
        dayjs(firstEntry?.date).isSame(referenceDate, "day") &&
        firstEntry?.equipment === selectedService.equipment[0]?._id &&
        firstEntry?.service_id === selectedService.service_id_raw
      ) {
        storedMovements = firstEntry.movements ?? [];
      }
      // --- API movements, parsed into waresMap shape ---
      if (storedMovements.length === 0) {
        const movementsResponse = await apiClient.getMany("/goods/movements", {
          params: {
            filter: {
              equipment: selectedService.equipment[0]._id,
              service_id: selectedService.service_id_raw,
            },
          },
        });
        // não ponho erros aqui porque não é necessário. caso isto dê trigger em offline dará erro
        const apiMovements = movementsResponse.status === true && movementsResponse.data.length > 0 ? parseApiMovements(movementsResponse.data) : [];
        storedMovements = apiMovements;
      }
      // --- Stocks ---
      const stocksRes = await apiClient.getOffline("/goods/stocks");
      if (!stocksRes.status) {
        notification.error({ message: "Erro", description: "Erro ao carregar os stocks" });
        await apiClient.post("/error-capture", {
          error: stocksRes.data,
          action: "/ConsumptionsSales/loadData/ getOffline /goods/stocks",
          timestamp: new Date().toISOString(),
        });
        return;
      }

      const equipmentIds = selectedService.equipment.map((e) => e._id);
      const stocks = stocksRes.data.filter((s) => s.wares?.type === "goods" && equipmentIds.includes(s.equipment?._id));

      if (!isMountedRef.current) return;
      setStocks(stocks);

      // --- Wares ---
      const wareIds = stocks.map((s) => s.wares._id);
      const waresRes = await apiClient.getMany("/goods/wares");
      if (!waresRes.status) {
        await apiClient.post("/error-capture", {
          error: waresRes.data,
          action: "/ConsumptionsSales/loadData/ getMany /goods/wares",
          timestamp: new Date().toISOString(),
        });
        notification.error({ message: "Erro", description: "Erro ao carregar os artigos" });
        return;
      }

      const wares = waresRes.data.filter((w) => wareIds.includes(w._id));

      if (!isMountedRef.current) return;
      setWares(wares);

      // --- Build and merge movements ---
      // Priority: storedMovements > apiMovements > initialMovements (zeros)
      const initialMovements = wares.map((ware) => ({
        wares: ware._id,
        consumption: { quantity: 0, notes: "" },
        sale_movement_unit: {
          payment_method_money: { quantity: 0, notes: "" },
          payment_method_card: { quantity: 0, notes: "" },
        },
        sale_sale_unit: {
          payment_method_money: { quantity: 0, notes: "" },
          payment_method_card: { quantity: 0, notes: "" },
        },
      }));

      // const finalMovements = mergeMovements(mergeMovements(initialMovements, apiMovements), storedMovements);
      const finalMovements = mergeMovements(initialMovements, storedMovements);

      form.setFieldsValue({ movements: finalMovements });

      if (!isMountedRef.current) return;

      // Error handling
    } catch (error) {
      if (!isMountedRef.current) return;
      notification.error({ message: "Erro", description: "Erro ao carregar dados" });
      await apiClient.post("/error-capture", {
        error: error,
        action: "/ConsumptionsSales/loadData/catch",
        timestamp: new Date().toISOString(),
      });

      // This is Sparta Mode
    } finally {
      setIsLoading(false);
    }
  };

  const getStockQuantity = (wareId) => {
    if (isNilOrEmpty(selectedService) || isNilOrEmpty(selectedService?.equipment?.[0]?._id)) return 0;
    const equipment = selectedService.equipment[0]._id;
    if (!equipment) return 0;
    const stock = stocks.find((s) => s.wares._id === wareId && s.equipment._id === equipment);
    const baseQuantity = stock?.counted_quantity || 0;

    // Calculate total consumed/sold for this ware
    const movement = getMovementByWare(movements, wareId);
    if (!movement) return safeParseInt(baseQuantity, 0).toFixed(2);

    const ware = wares.find((w) => w._id === wareId);
    const conversionFactor = ware?.sales_conversion_factor || 1;

    const consumed = movement.consumption?.quantity / conversionFactor || 0;

    // Movement unit sales
    const soldMovementUnit =
      (movement.sale_movement_unit?.payment_method_money?.quantity || 0) + (movement.sale_movement_unit?.payment_method_card?.quantity || 0);

    // Sale unit sales
    const soldSaleUnitRaw = (movement.sale_sale_unit?.payment_method_money?.quantity || 0) + (movement.sale_sale_unit?.payment_method_card?.quantity || 0);

    // Convert sale units to movement units
    const soldSaleUnitConverted = soldSaleUnitRaw / conversionFactor;

    const totalDeduction = consumed + soldMovementUnit + soldSaleUnitConverted;
    const adjustedQuantity = baseQuantity - totalDeduction;

    return adjustedQuantity.toFixed(2);
  };

  const getSalePrice = (ware, unit, referenceDate) => {
    if (!ware?.sell_price?.length) return 0;

    const pricesByUnit = ware.sell_price.filter((p) => p.unit === unit);
    // Fallback: sale_unit → movement_unit
    if (unit === "sale_unit" && pricesByUnit.length === 0) {
      const fallbackPrices = ware.sell_price.filter((p) => p.unit === "movement_unit");
      return getCurrentPrice(fallbackPrices, referenceDate);
    }

    return getCurrentPrice(pricesByUnit, referenceDate);
  };

  const totalAmounts = (currentMovements, currentWares) => {
    if (!currentMovements || !currentWares?.length) {
      return { payment_method_money: 0, payment_method_card: 0, total: 0 };
    }

    const referenceDate = selectedDate?.toDate();

    let moneyTotal = 0;
    let cardTotal = 0;

    currentMovements.forEach((m) => {
      const ware = currentWares.find((w) => w._id === m.wares);
      if (!ware) return;

      // Movement unit - money
      const moneyMU = m.sale_movement_unit?.payment_method_money?.quantity || 0;
      if (moneyMU) {
        const price = getSalePrice(ware, "movement_unit", referenceDate);
        moneyTotal += price * moneyMU;
      }

      // Movement unit - card
      const cardMU = m.sale_movement_unit?.payment_method_card?.quantity || 0;
      if (cardMU) {
        const price = getSalePrice(ware, "movement_unit", referenceDate);
        cardTotal += price * cardMU;
      }

      // Sale unit - money
      const moneySU = m.sale_sale_unit?.payment_method_money?.quantity || 0;
      if (moneySU) {
        const price = getSalePrice(ware, "sale_unit", referenceDate);
        moneyTotal += price * moneySU;
      }

      // Sale unit - card
      const cardSU = m.sale_sale_unit?.payment_method_card?.quantity || 0;
      if (cardSU) {
        const price = getSalePrice(ware, "sale_unit", referenceDate);
        cardTotal += price * cardSU;
      }
    });

    return {
      payment_method_money: moneyTotal,
      payment_method_card: cardTotal,
      total: moneyTotal + salesTotals,
    };
  };

  /* ---------------------------
     Modal handlers
  ---------------------------- */
  const handleOpenModal = (ware) => {
    setSelectedWare(ware);
    setSaleDrawerVisible(true);
  };

  const handleSalesDrawerConfirm = (values) => {
    const { type, unit, payment_method, quantity, notes } = values;
    const wareId = selectedWare._id;

    const currentMovements = form.getFieldValue("movements") || [];

    let movementKey;

    if (type === "consumption") {
      movementKey = "consumption";
    } else if (type === "sale" && unit === "movement_unit") {
      movementKey = "sale_movement_unit";
    } else if (type === "sale" && unit === "sale_unit") {
      movementKey = "sale_sale_unit";
    }

    if (!movementKey) return;

    const updatedMovements = currentMovements.map((m) => {
      if (m.wares !== wareId) return m;

      const newMovement = { ...m };

      if (movementKey === "consumption") {
        const currentQty = newMovement.consumption.quantity || 0;

        newMovement.consumption = {
          quantity: parseInt(currentQty) + parseInt(quantity),
          notes,
        };
      } else {
        const currentQty = newMovement[movementKey][payment_method]?.quantity || 0;

        newMovement[movementKey] = {
          ...newMovement[movementKey],
          [payment_method]: {
            quantity: parseInt(currentQty) + parseInt(quantity),
            notes,
          },
        };
      }

      return newMovement;
    });

    form.setFieldsValue({
      movements: updatedMovements,
    });
  };

  const handleSaleMinusClick = (ware, unitKey) => {
    const movement = getMovementByWare(movements, ware._id);

    if (!movement) return;

    const unit = movement[unitKey];
    if (!unit) return;

    const activeMethods = getActivePaymentMethods(unit);

    if (activeMethods.length === 0) return;

    if (activeMethods.length === 1) {
      const methodKey = activeMethods[0].key;

      updateQuantity(ware._id, `${unitKey}.${methodKey}`, activeMethods[0].quantity - 1);
      return;
    }

    setRemoveTarget({
      ware,
      unitKey,
      methods: activeMethods,
    });

    setRemoveModalVisible(true);
  };

  const getActivePaymentMethods = (movementUnit) => {
    if (!movementUnit) return [];

    return Object.entries(movementUnit)
      .filter(([, data]) => data && typeof data.quantity === "number" && data.quantity > 0)
      .map(([key, data]) => ({
        key, // payment_method_money, payment_method_card, ... // Change later
        ...data,
      }));
  };

  const updateQuantity = (wareId, movementPath, value) => {
    const path = movementPath.split(".");
    const newValue = Math.max(0, value);

    const currentMovements = form.getFieldValue("movements") || [];

    const updatedMovements = currentMovements.map((m) => {
      if (m.wares !== wareId) return m;

      const newMovement = { ...m };

      let ref = newMovement;

      for (let i = 0; i < path.length; i++) {
        const key = path[i];

        if (i === path.length - 1) {
          ref[key] = {
            ...ref[key],
            quantity: newValue,
          };
        } else {
          ref[key] = { ...ref[key] };
          ref = ref[key];
        }
      }

      return newMovement;
    });

    form.setFieldsValue({
      movements: updatedMovements,
    });
  };

  /* ---------------------------
     SUBMITs
  ---------------------------- */
  const handleSubmit = async () => {
    try {
      if (!selectedService) return;
      const selectedEquipmentId = selectedService?.equipment?.[0]?._id || null;
      const selectedServiceIdRaw = selectedService?.service_id_raw || null;
      const selectedServiceDate = selectedDate ? dayjs(selectedDate).toDate() : dayjs(selectedService?.final_start_date).toDate();
      if (!selectedEquipmentId || !selectedServiceIdRaw) return;

      const values = form.getFieldValue();
      if (isNilEmptyOrZeroLength(values.movements)) return;
      setIsLoading(true);
      const currentWares = waresRef.current;

      // Filter out movements with all quantities at 0
      const filteredMovements = values.movements.filter((movement) => {
        return (
          movement.consumption.quantity > 0 ||
          movement.sale_movement_unit.payment_method_money.quantity > 0 ||
          movement.sale_movement_unit.payment_method_card.quantity > 0 ||
          movement.sale_sale_unit.payment_method_money.quantity > 0 ||
          movement.sale_sale_unit.payment_method_card.quantity > 0
        );
      });
      const cashTotals = totalAmounts(filteredMovements, currentWares);

      const payload = {
        ...values,
        date: selectedServiceDate,
        service_id: selectedServiceIdRaw,
        equipment: selectedEquipmentId,
        movements: filteredMovements,
        moneyBoxTotal: cashTotals.total || 0,
      };

      if (payload.movements.length === 0) {
        console.log("Sem Movimentos");
        return;
      }

      // Update local table of stocks. Only runs if offline

      const newStocks = stocksRef.current;

      // this runs if offline and when
      const newStocksInStore = await createNewStocksInStoreByContext(newStocks, {
        user_id: auth?.data?._id || null,
        service_id_raw: selectedService?.service_id_raw || null,
      });
      if (newStocksInStore.status === false) {
        notification.error({
          title: "Erro",
          description: "Erro ao atualizar as existências",
        });
        await apiClient.post("/error-capture", {
          error: newStocksInStore.data,
          action: "/ConsumptionsSales/handleSubmit/ createNewStocksInStore",
          timestamp: new Date().toISOString(),
        });
        return;
      }

      // await updateStocksStore(newStocks);
      stocksRef.current = [];

      const movementDraftId = `movements_${auth?.data?._id || "nouser"}_${payload.service_id}_${payload.equipment}_${dayjs(payload.date).format("YYYY-MM-DD")}`;
      const saveMovementsToStoreResponse = await saveMovementsToStore({
        method: "POST",
        url: "/goods/consumptions-sales",
        data: payload,
        id: movementDraftId,
        user_id: auth?.data?._id || null,
        service_id_raw: payload.service_id,
        equipment: payload.equipment,
        date: payload.date,
        updated_at: new Date().toISOString(),
      });
      if (saveMovementsToStoreResponse.status === false) {
        notification.error({
          title: "Erro",
          description: "Erro ao guardar movimentos",
        });
        await apiClient.post("/error-capture", {
          error: saveMovementsToStoreResponse.data,
          action: "/ConsumptionsSales/handleSubmit/ saveMovementsToStore",
          timestamp: new Date().toISOString(),
        });
        return;
      }

      // Capture the consumption/sale location (best-effort; never blocks the action).
      locationTracker.captureActionLocation({
        service_id_raw: selectedServiceIdRaw,
        equipment_id: selectedEquipmentId,
        action: LOCATION_ACTIONS.CONSUMPTION_SALE,
        action_reference: generateActionReference(),
      });

      // Send info capture to the server for debugging purposes
      await apiClient.post("/info-capture", {
        extraData: {
          location: "src/views/app-views/goods/consumptions-sales/index.js",
          timestamp: new Date().toISOString(),
        },
        info: {
          location: "src/views/app-views/goods/consumptions-sales/index.js",
          timestamp: new Date().toISOString(),
          service_id: selectedService?.service_id_raw || null,
          equipment: selectedService?.equipment?.[0]?._id || null,
          payload: payload,
        },
        subject: "Boats: Mobile Consumptions",
        message: `Boats: Movements saved to local store (${authUserName})`,
      });

      // Error handling
    } catch (error) {
      notification.error({
        title: "Erro",
        description: "Erro ao guardar movimentos",
      });
      await apiClient.post("/error-capture", {
        error: {
          message: error?.message || "Unknown error",
          stack: error?.stack || null,
          stringified: String(error),
        },
        action: "/ConsumptionsSales/handleSubmit/catch",
        timestamp: new Date().toISOString(),
      });

      // This is Sparta Mode
    } finally {
      setIsLoading(false);
    }
  };

  submitOnUnmountRef.current = handleSubmit;

  useEffect(() => {
    return () => {
      submitOnUnmountRef.current();
    };
  }, []);

  return (
    <PageLayoutWrapper title="Movimentos de Consumo e Venda" sales={true}>
      <Spin spinning={isLoading} fullscreen />
      <Card size="small">
        <Form form={form} layout="vertical" onFinish={handleSubmit} className="compact-form">
          <Form.Item name="movements" hidden>
            <Input />
          </Form.Item>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #f0f0f0",
              marginBottom: 8,
              paddingBottom: 8,
            }}
          >
            {/* LEFT SIDE */}
            <Space>
              {hasCatering && (
                <Button danger onClick={() => setCateringDrawerVisible(true)}>
                  Catering incluído
                </Button>
              )}

              {productForService && (
                <Button danger onClick={() => setProductForServiceDrawerVisible(true)}>
                  Transfer
                </Button>
              )}
            </Space>

            {/* RIGHT SIDE (Totals) */}
            <Space>
              <Text>Dinheiro: {formatCurrency(totalAmounts(movements, wares).payment_method_money)}</Text>
              <Text>Cartão: {formatCurrency(totalAmounts(movements, wares).payment_method_card)}</Text>
              <Text strong style={{ fontSize: 16 }}>
                Caixa: Fundo + {formatCurrency(totalAmounts(movements, wares).total)}
              </Text>
            </Space>
          </div>

          {/* Header */}
          <Row gutter={2} align="stretch" style={{ marginBottom: 8 }}>
            {/* Artigo */}
            <Col md={5}>
              <Text strong>Artigo</Text>
            </Col>

            {/* GROUP 1 */}
            <Col md={8}>
              <Row gutter={4}>
                <Col span={5} style={{ textAlign: "center" }}>
                  <Text strong>Un. Stock</Text>
                </Col>
                <Col span={4} style={{ textAlign: "center" }}>
                  <Text strong>Q. Stock</Text>
                </Col>
                <Col span={11} style={{ textAlign: "center" }}>
                  <Text style={{ marginLeft: -35 }} strong>
                    Venda
                  </Text>
                </Col>
              </Row>
            </Col>

            {/* GROUP 2 (Consumo + Venda Sale Unit + Button) */}
            <Col md={9}>
              <Row gutter={4}>
                <Col span={5} style={{ textAlign: "center" }}>
                  <Text strong>Un. Venda</Text>
                </Col>
                <Col span={7} style={{ textAlign: "center" }}>
                  <Text strong>Consumo</Text>
                </Col>
                <Col span={10} style={{ textAlign: "center" }}>
                  <Text strong style={{ marginLeft: -35 }}>
                    Venda
                  </Text>
                </Col>
              </Row>
            </Col>
          </Row>

          {/* Rows */}
          {wares.map((ware) => {
            const movement = getMovementByWare(movements, ware._id);

            return (
              <Row key={ware._id} gutter={2} align="stretch" style={{ padding: "2px 0", borderBottom: "1px solid #f0f0f0" }}>
                {/* Artigo */}
                <Col md={5} style={{ display: "flex", alignItems: "center" }}>
                  <Text strong>{ware.description}</Text>
                </Col>

                {/* GROUP 1 */}
                <Col md={8}>
                  <div style={groupBoxStyle1}>
                    <Row gutter={2} align="middle">
                      <Col span={5} style={{ textAlign: "center" }}>
                        <Text>{ware.movement_unit}</Text>
                      </Col>

                      <Col span={4} style={{ textAlign: "center" }}>
                        <Text style={{ color: getStockQuantity(ware._id) < 0 ? "red" : "inherit" }}>{getStockQuantity(ware._id)}</Text>
                      </Col>

                      <Col span={6}>
                        {ware.sale_unit ? (
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <InlineQuantity
                              style={groupBoxStyle1.background}
                              value={
                                movement?.sale_movement_unit?.payment_method_money?.quantity + movement?.sale_movement_unit?.payment_method_card?.quantity || 0
                              }
                              onPlus={() =>
                                updateQuantity(
                                  ware._id,
                                  "sale_movement_unit.payment_method_money",
                                  movement.sale_movement_unit.payment_method_money.quantity + 1,
                                )
                              }
                              onMinus={() => handleSaleMinusClick(ware, "sale_movement_unit")}
                            />
                            {formatCurrency(getSalePrice(ware, "movement_unit", selectedDate))}
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            {/* Para prevenir desformatação da linha vazia*/}
                            <div style={{ height: 40 }}></div>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                </Col>

                {/* GROUP 2 */}
                <Col md={9}>
                  <div style={groupBoxStyle2}>
                    <Row gutter={2} align="middle">
                      {/* Un Venda */}
                      <Col span={4} style={{ textAlign: "center" }}>
                        <Text>{ware.sale_unit ? ware.sale_unit : ware.movement_unit}</Text>
                      </Col>
                      {/* Consumo */}
                      <Col span={8}>
                        <InlineQuantity
                          style={groupBoxStyle2.background}
                          value={movement?.consumption?.quantity || 0}
                          onPlus={() => updateQuantity(ware._id, "consumption", movement.consumption.quantity + 1)}
                          onMinus={() => updateQuantity(ware._id, "consumption", movement.consumption.quantity - 1)}
                        />
                      </Col>
                      {/* Venda */}
                      <Col span={10}>
                        {ware.sale_unit ? (
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <InlineQuantity
                              style={groupBoxStyle2.background}
                              value={movement?.sale_sale_unit?.payment_method_money?.quantity + movement?.sale_sale_unit?.payment_method_card?.quantity || 0}
                              onPlus={() =>
                                updateQuantity(ware._id, "sale_sale_unit.payment_method_money", movement.sale_sale_unit.payment_method_money.quantity + 1)
                              }
                              onMinus={() => handleSaleMinusClick(ware, "sale_sale_unit")}
                            />
                            {formatCurrency(getSalePrice(ware, "sale_unit", selectedDate))}
                          </div>
                        ) : (
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <InlineQuantity
                              style={groupBoxStyle2.background}
                              value={
                                movement?.sale_movement_unit?.payment_method_money?.quantity + movement?.sale_movement_unit?.payment_method_card?.quantity || 0
                              }
                              onPlus={() =>
                                updateQuantity(
                                  ware._id,
                                  "sale_movement_unit.payment_method_money",
                                  movement.sale_movement_unit.payment_method_money.quantity + 1,
                                )
                              }
                              onMinus={() => handleSaleMinusClick(ware, "sale_movement_unit")}
                            />
                            {formatCurrency(getSalePrice(ware, "movement_unit", selectedDate))}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </div>
                  {/* Button */}
                </Col>
                <Col span={2}>
                  <Button size="large" className="shopping-button" block onClick={() => handleOpenModal(ware)}>
                    <PlusCircleOutlined style={{ fontSize: "24px", color: "#609686" }} /> Info
                  </Button>
                </Col>
              </Row>
            );
          })}
        </Form>
      </Card>

      <SalesDrawer
        visible={saleDrawerVisible}
        wareData={selectedWare}
        selectedDate={selectedDate}
        initialValues={selectedWare ? movements?.[selectedWare._id] : {}}
        onClose={() => setSaleDrawerVisible(false)}
        onConfirm={handleSalesDrawerConfirm}
      />
      <CateringDrawer selectedService={selectedService} visible={cateringDrawerVisible} onClose={() => setCateringDrawerVisible(false)} />
      <TransferDrawer selectedService={selectedService} visible={productForServiceDrawerVisible} onClose={() => setProductForServiceDrawerVisible(false)} />

      <RemoveQuantityModal
        visible={removeModalVisible}
        ware={removeTarget?.ware}
        unitKey={removeTarget?.unitKey}
        movement={removeTarget ? getMovementByWare(movements, removeTarget.ware._id) : null}
        onClose={() => setRemoveModalVisible(false)}
        onConfirm={(paymentMethodKey) => {
          const { ware, unitKey } = removeTarget;
          const current = movements.find((m) => m.wares === ware._id)[unitKey][paymentMethodKey].quantity || 0;

          updateQuantity(ware._id, `${unitKey}.${paymentMethodKey}`, current - 1);
        }}
      />
    </PageLayoutWrapper>
  );
}

export default ConsumptionsSales;
